import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Link2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { CheckInBoard } from "@/components/CheckInBoard";
import { prettyDate } from "@/components/LeadPractice";
import { Button } from "@/components/ui/button";

function VolunteerLink({ pid, date, vol, reload }) {
  const url = vol?.token ? `${window.location.origin}/crew/${vol.token}` : "";
  const set = async (body) => {
    try { await api.post(`/checkin/staff/${pid}/${date}/volunteer-link`, body); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const copy = () => { navigator.clipboard?.writeText(`${url}\nCode: ${vol.code}`).then(() => toast.success("Link and code copied")).catch(() => toast.error("Copy failed")); };
  return (
    <div className="rounded-2xl bg-white border p-4 space-y-2" data-testid="volunteer-link">
      <div className="flex items-center justify-between gap-2"><p className="font-head font-bold flex items-center gap-2"><Link2 className="w-4 h-4 text-teal" />Volunteer check-in link</p>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="volunteer-link-toggle" className="w-4 h-4 accent-teal" checked={!!vol?.enabled} onChange={(e) => set({ enabled: e.target.checked })} />On</label></div>
      {vol?.enabled ? <>
        <p className="text-xs break-all text-muted-foreground" data-testid="volunteer-link-url">{url}</p>
        <p className="text-sm">Code: <span className="font-mono font-bold text-lg tracking-widest" data-testid="volunteer-link-code">{vol.code}</span></p>
        <div className="flex gap-2"><Button size="sm" variant="outline" className="rounded-full" data-testid="volunteer-link-copy" onClick={copy}><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
          <Button size="sm" variant="ghost" className="rounded-full" data-testid="volunteer-link-regenerate" onClick={() => window.confirm("Make a new link and code? The old one stops working.") && set({ enabled: true, regenerate: true })}><RefreshCw className="w-3.5 h-3.5 mr-1" />New link & code</Button></div>
        <p className="text-[11px] text-muted-foreground">Works only on {prettyDate(date)}. Volunteers see first names and last initials only.</p>
      </> : <p className="text-xs text-muted-foreground">Turn on to let volunteers check dancers in without an account.</p>}
    </div>
  );
}

export default function CheckIn() {
  const { pid, date } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/checkin/staff/${pid}/${date}`).then((r) => setData(r.data)).catch((e) => setData({ error: e.response?.data?.detail || "Could not load" })), [pid, date]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const toggle = async (r) => {
    setBusy(r.dancer_id);
    try { await api.post(`/checkin/staff/${pid}/${date}/mark`, { dancer_id: r.dancer_id, present: !r.checked_in }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(null); }
  };
  return (
    <div className="max-w-xl mx-auto" data-testid="checkin-page">
      <Link to="/production?tab=rehearsals" className="text-sm text-teal-dark flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" />Dress Rehearsals</Link>
      <h1 className="font-head text-3xl font-bold">Check-in</h1>
      {data?.error ? <p className="mt-4 text-red-600">{data.error}</p> : !data ? <p className="mt-4 text-muted-foreground">Loading…</p> : <>
        <p className="text-muted-foreground mb-4">{data.production_name} · {prettyDate(date)} · dancers called {data.call_minutes} min before their first slot</p>
        {data.volunteer && <div className="mb-4"><VolunteerLink pid={pid} date={date} vol={data.volunteer} reload={load} /></div>}
        <CheckInBoard data={data} onToggle={toggle} busyId={busy} sticky="top-16 lg:top-0" />
      </>}
    </div>
  );
}
